/**
 * Types supporting map layer attributes
 */

// Typings to support GeoJSON data
// See https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/geojson/index.d.ts
import GeoJSON, { Position } from 'geojson';

import type { StacFeature } from './STAC';
import { Layer, MVTLayer } from '@methanesat/maps';

export enum AreaEmissionsProducts {
    l3 = 'l3',
    l4 = 'l4'
}
export enum MethaneEmissionsProducts {
    l3 = 'l3',
    l4 = 'l4',
    target = 'target',
    plume = 'plume'
}

export const isMVTLayer = (layer: Layer): layer is MVTLayer => {
    return layer.props && 'uniqueIdProperty' in layer.props;
};

/** Type guard to check whether a selected product is of the `AreaEmissionsProducts` type */
export function isAreaEmissionsProduct(value: string): value is AreaEmissionsProducts {
    return Object.values<string>(AreaEmissionsProducts).includes(value);
}

export const areaEmissionsProductValues: (keyof typeof AreaEmissionsProducts)[] = Object.keys(
    AreaEmissionsProducts
) as (keyof typeof AreaEmissionsProducts)[];

export type AreaEmissionsProduct = (typeof areaEmissionsProductValues)[number];

/** All methane layer ids. */
export enum MethaneLayerIds {
    targets = 'targets',
    plumeEmissionRate = 'plume-emission-rate',
    areaEmissionRaster = 'area-emission-raster',
    areaEmissionRasterHighlight = 'highlight-for-area-emissions-raster'
}

/** All source types for DrawerWithCards */
export enum SourceIds {
    dispersed = 'dispersed',
    distinct = 'distinct'
}

/**
 * Methane feature types, as represented in the app url
 */
export enum UrlFeatureTypes {
    Plume = 'point-source',
    AreaEmission = 'ae',
    Target = 'target',
    TotalOnly = 'total-only'
}
export function isUrlFeatureType(data: unknown): data is UrlFeatureTypes {
    if (typeof data !== 'string') return false;
    return Object.values(UrlFeatureTypes).includes(data as UrlFeatureTypes);
}

/**
 * Converts the app's url parameter `feature-type` to its corresponding layer id
 */
export const UrlFeatureTypeToLayerId = new Map<UrlFeatureTypes, MethaneLayerIds>([
    [UrlFeatureTypes.Plume, MethaneLayerIds.plumeEmissionRate],
    [UrlFeatureTypes.AreaEmission, MethaneLayerIds.areaEmissionRaster],
    [UrlFeatureTypes.Target, MethaneLayerIds.targets]
]);

/**
 * Converts a methane layer id into the corresponding value for url parameter `feature-type`
 * inversion of UrlFeatureTypeToLayerId
 */
export const LayerIdToUrlFeatureType = new Map(
    Array.from(UrlFeatureTypeToLayerId, (a) => a.reverse() as [MethaneLayerIds, UrlFeatureTypes])
);

export interface LoadedData {
    [key: string]: number | string;
}

export interface TargetTypeProperties {
    /** Unique ID for this specific target. */
    target_id: string;
    /** GeoJSON coordinates of the target's geometry */
    coordinates?: GeoJSON.Position[];
    /** Pixel size in km². */
    pixel_size_km2?: number;
    processor_version?: string;
}
export type TargetTypeFeature = GeoJSON.Feature<GeoJSON.Polygon, TargetTypeProperties>;

export interface TargetFeatureProperties extends TargetTypeProperties {
    /** Instrument used for this measurement. */
    instrument?: string;
    /** Location where the measurement was taken. */
    location?: string;
    /** Total size of the target in km². */
    sizeKm2?: number;
    /** Unique ID for this specific target. */
    targetId?: string;
    /** End of the data capture interval in ISO-8601 UTC format. */
    timeEnd?: string;
    /** Start of the data capture interval in ISO-8601 UTC format. */
    timeStart?: string;
    /** Upper confidence interval in kg/hr. */
    totalHighKgHr?: number;
    /** Total measured emissions of the entire target. */
    totalKgHr?: number;
    /** Lower confidence interval in kg/hr. */
    totalLowKgHr?: number;
    /** Total number of discernable plumes in the target. */
    pointSourceCount?: number;
    /**
     * Sum total kg/hr of all discernable plumes in the target, used to determine their contribution
     * relative to overall area emissions.
     */
    pointSourceTotalKgHr?: number;
}
export type TargetFeature = GeoJSON.Feature<GeoJSON.Polygon, TargetFeatureProperties>;

/**
 * These values describe the flux for the entire capture footprint (not limited to target bounds).
 */
export interface TotalCaptureFluxProperties {
    /** Total measured emissions of the entire capture. */
    mean_total_flux: number;
    /** Lower confidence interval in kg/hr. */
    total_flux_lower_bound: number;
    /** Upper confidence interval in kg/hr. */
    total_flux_upper_bound: number;
}

/**
 * These values describe the flux inside the target polygon bounds only (not the whole capture).
 * New/reprocessed DPP outputs include these fields in addition to the total-capture flux fields.
 */
export interface TargetCaptureFluxProperties extends TotalCaptureFluxProperties {
    /** Measured emissions inside the target bounds only. */
    target_total_flux_mean: number;
    /** Lower confidence interval in kg/hr. */
    target_total_flux_lower_bound: number;
    /** Upper confidence interval in kg/hr. */
    target_total_flux_upper_bound: number;
}

export type DPPCaptureFluxProperties = TargetCaptureFluxProperties | TotalCaptureFluxProperties;

// updated Capture Type
export type DPPCaptureProperties = DPPCaptureFluxProperties & {
    created: string;
    /** DPP data: End of the data capture interval in ISO-8601 UTC format. */
    time_coverage_end: string;
    /** DPP data: Start of the data capture interval in ISO-8601 UTC format. */
    time_coverage_start: string;
};
export interface NonDPPCaptureProperties {
    /** Upper confidence interval in kg/hr. For NON-DPP processed captures */
    net_total_high?: number;
    /** Lower confidence interval in kg/hr. For NON-DPP processed captures */
    net_total_low?: number;
    /** Total measured emissions of the entire capture. For NON-DPP processed captures. */
    net_total: number;
    /** NON-DPP data: End of the data capture interval in ISO-8601 UTC format. */
    end_datetime: string;
    /** NON-DPP data: Start of the data capture interval in ISO-8601 UTC format. */
    start_datetime: string;
}
export type CaptureFeatureProperties<AdditionalSourceTypeProperties = DPPCaptureProperties | NonDPPCaptureProperties> =
    TargetTypeProperties &
        AdditionalSourceTypeProperties & {
            /** Basin where capture taken */
            basin?: string;
            location?: string;
            /** Descriptive capture title */
            title?: string;
            /** Description */
            description?: string;
            /** States covered by capture */
            states?: string[];
            /** Country locating capture */
            country?: 'string';
            /** Total number of discernable plumes in the capture. */
            ps_count?: number;
            /**
             * Sum total kg/hr of all discernable plumes in the capture, used to determine their contribution
             * relative to overall area emissions.
             */
            ps_total?: number;
            /** Total size of the capture in km². */
            size_km2?: number;
            /** Total measured emissions of just the area emissions. */
            area_total?: number;
            /** Instrument used for this measurement. */
            instrument: string;
            /** Unique ID for this specific capture. */
            capture_id?: string;
            /** date of capture */
            datetime: string;
            /** MethaneSAT collection ID, different from the STAC collection ID */
            collection_id: string;
            /** End of the data capture interval in ISO-8601 UTC format. */
            end_datetime: string;
            /** Start of the data capture interval in ISO-8601 UTC format. */
            start_datetime: string;
            /** Id of target */
            target_id: string;
            /** Geometry of the original target bounds.
             * Not a proper geojson polygon; must be wrapped in another array to use as such */
            target_geometry: Position[];
            /** used for combined targets, may be the same as original target_id */
            target_group_id?: string;
            /** used for combined targets, may be the same as original target_geometry
             * Not a proper geojson polygon; must be wrapped in another array to use as such */
            target_group_geometry?: Position[];
        };
export type CaptureFeature = StacFeature & GeoJSON.Feature<GeoJSON.Polygon, CaptureFeatureProperties>;
export type CaptureFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Polygon, CaptureFeatureProperties>;

export function isDPPCaptureFeature(
    props: CaptureFeatureProperties<object>
): props is CaptureFeatureProperties<DPPCaptureFluxProperties> {
    if (!props || !('created' in props) || !props.created) return false;

    return (
        'mean_total_flux' in props &&
        props.mean_total_flux != null &&
        'total_flux_lower_bound' in props &&
        props.total_flux_lower_bound != null &&
        'total_flux_upper_bound' in props &&
        props.total_flux_upper_bound != null
    );
}

export function isNonDPPCaptureFeature(
    props: CaptureFeatureProperties<object>
): props is CaptureFeatureProperties<NonDPPCaptureProperties> {
    if (props && 'net_total' in props && props.net_total) return true;
    return false;
}

export function isCaptureFeature(data: unknown): data is CaptureFeature {
    if (!data || typeof data !== 'object') return false;
    if (Array.isArray(data)) return false;
    return 'geometry' in data && 'properties' in data && isCaptureProperties(data.properties);
}

/**
 * If the proprties are of a Capture
 * @param data
 * @returns boolean if data is a capture
 */
export function isCaptureProperties(data: unknown): data is CaptureFeatureProperties {
    if (!data || typeof data !== 'object') return false;
    return 'target_id' in data;
}

/**
 * Mean emissions in a target (mean = total emissions / target size)
 */
export type kgHrKm2 = number;

/**
 * Total emissions. Used for targets
 */
export type kgHr = number;

export enum EmissionUnits {
    kgHr = 'kgHr',
    kgHrKm2 = 'kgHrKm2'
}
